import { productEvent } from "@shared/analytics/productEvents";
import { DataTable } from "@solomon-labs/design/components/data";
import type { DataTableColumn } from "@solomon-labs/design/components/data";
import {
    ArrowShortUpRightIcon,
    DocIcon,
    ShieldIcon,
    ThreeCoinsIcon,
    TooltipIcon
} from "@solomon-labs/design/components/icons";
import {
    SectionHeading,
    SectionStack
} from "@solomon-labs/design/components/layout";
import {
    Badge,
    Button,
    buttonClasses,
    ButtonIcon,
    Callout,
    Card,
    Text,
    ToggleGroup,
    ToggleGroupItem,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger
} from "@solomon-labs/design/components/ui";
import React from "react";
import { Link } from "react-router-dom";

// The Solomon symbol alone (brand/brandmark). The manifest's "logomark" alias
// is the full lockup, which is never used inline or below ~120px wide.
import solomonBrandmark from "../../../../../../../.claude/design-system/assets/brand/brandmark.svg";
// Asymmetric Research runs STRIDE: its full-colour lockup (logo/asymmetric-research
// in the asset manifest) heads the block, as Blockaid's does in OnchainMonitoring.
import asymmetricResearchLogo from "../../../../../../../.claude/design-system/assets/logos/partners/asymmetric-research.svg";
import usdvTokenIcon from "../../../../../../../.claude/design-system/assets/tokens/USDv.png";
import {
    AUDITS,
    CHANCERY_PROGRAM_ID,
    ONCHAIN_AUTHORITIES,
    RISK_DISCLOSURE_URL,
    USDV_MINT,
    VULNERABILITY_REPORT_EMAIL
} from "../securityEvidence";
import type { AuthorityConfiguration } from "../securityEvidence";

import { abbreviateAddress } from "./abbreviateAddress";
import { AuditLogo } from "./auditLogos";
import { ControlInspection } from "./ControlInspection";
import { solscanAccountUrl, solscanTokenUrl } from "./explorerLinks";
import { OnchainMonitoring } from "./OnchainMonitoring";
import { SecurityAddress } from "./SecurityAddress";
import { SecurityGovernanceWorkflows } from "./SecurityGovernanceWorkflows";

const AUTHORITY_ROWS: AuthorityConfiguration[] = [...ONCHAIN_AUTHORITIES];
const AUTHORITY_FOOTNOTE = AUTHORITY_ROWS.find(
    (authority) => authority.id === "insurance"
)?.note;
const AUTHORITY_FOOTNOTE_ID = "authority-insurance-note";

const PROGRAM_ADDRESSES: readonly {
    name: string;
    address: string;
    explorerHref: string;
    icon?: string;
}[] = [
    {
        name: "USDv Controls Program",
        address: CHANCERY_PROGRAM_ID,
        explorerHref: solscanAccountUrl(CHANCERY_PROGRAM_ID),
        icon: solomonBrandmark
    },
    {
        name: "USDv Token Mint",
        address: USDV_MINT,
        explorerHref: solscanTokenUrl(USDV_MINT),
        icon: usdvTokenIcon
    }
];

/**
 * The help strip that closes the Security tab, after the wiki docs' "Get
 * help" band: rules above and below, one row of links. Each is a link Button
 * at sm (the small text variant) with a leading icon, a trailing link-out
 * arrow and the underline interaction.
 */
function SecurityHelpBar(): React.ReactElement {
    return (
        <nav
            aria-label="Security Help"
            className="mt-16 border-y border-line-base"
        >
            <div className="flex flex-wrap items-center gap-x-10 gap-y-2 py-3">
                <Button
                    variant="link"
                    size="sm"
                    interaction="underline"
                    leading={<ShieldIcon aria-hidden />}
                    trailing={<ArrowShortUpRightIcon aria-hidden />}
                    nativeButton={false}
                    render={<a href={`mailto:${VULNERABILITY_REPORT_EMAIL}`} />}
                >
                    Report a Vulnerability
                </Button>
                <Button
                    variant="link"
                    size="sm"
                    interaction="underline"
                    leading={<ThreeCoinsIcon aria-hidden />}
                    trailing={<ArrowShortUpRightIcon aria-hidden />}
                    nativeButton={false}
                    render={<Link to="/stats/reserves" />}
                >
                    View Reserves
                </Button>
                <Button
                    variant="link"
                    size="sm"
                    interaction="underline"
                    leading={<DocIcon aria-hidden />}
                    trailing={<ArrowShortUpRightIcon aria-hidden />}
                    nativeButton={false}
                    render={
                        <a
                            href={RISK_DISCLOSURE_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Read Risk Disclosures, opens in a new tab"
                        />
                    }
                >
                    Read Risk Disclosures
                </Button>
            </div>
        </nav>
    );
}

function CapabilityTooltip({
    authority
}: {
    authority: AuthorityConfiguration;
}): React.ReactElement {
    const [open, setOpen] = React.useState(false);
    const triggerId = `authority-capability-${authority.id}`;
    const contentId = `${triggerId}-description`;
    return (
        <Tooltip triggerId={triggerId} open={open} onOpenChange={setOpen}>
            <TooltipTrigger
                id={triggerId}
                aria-describedby={open ? contentId : undefined}
                closeOnClick={false}
                onClick={() => {
                    productEvent("stats:detail_viewed", {
                        entry_point: "stats_page",
                        section: "security",
                        control: "explanation"
                    });
                    setOpen(true);
                }}
                onFocus={() => setOpen(true)}
                render={
                    <ButtonIcon
                        size="md"
                        variant="transparent"
                        className="size-6 shrink-0"
                        icon={<TooltipIcon />}
                        aria-label={`${authority.name} Capability`}
                    />
                }
            />
            <TooltipContent
                id={contentId}
                role="tooltip"
                variant="tint"
                side="top"
                maxWidth="sm"
            >
                {authority.capability}
            </TooltipContent>
        </Tooltip>
    );
}

const AUTHORITY_COLUMNS: DataTableColumn<AuthorityConfiguration>[] = [
    {
        header: "Authority",
        unstyled: true,
        cell: (authority) => (
            <div className="flex min-w-0 items-center gap-1">
                <div className="min-w-0">
                    <Text
                        variant="small"
                        render={<span />}
                        aria-describedby={
                            authority.note ? AUTHORITY_FOOTNOTE_ID : undefined
                        }
                    >
                        {authority.name}
                        {authority.note ? (
                            <sup aria-hidden="true">1</sup>
                        ) : null}
                    </Text>
                </div>
                <CapabilityTooltip authority={authority} />
            </div>
        )
    },
    {
        header: "Multisig Address",
        align: "right",
        unstyled: true,
        cell: (authority) => (
            <a
                href={authority.multisigHref}
                onClick={() =>
                    productEvent("stats:link_clicked", {
                        entry_point: "stats_page",
                        section: "security",
                        link_type: "multisig"
                    })
                }
                target="_blank"
                rel="noopener noreferrer"
                title={authority.multisigAddress}
                aria-label={`${authority.name} Squads multisig ${authority.multisigAddress} on Solscan, opens in a new tab`}
                className={`${buttonClasses({ variant: "link", size: "sm" })} min-w-0 max-w-full gap-1`}
            >
                {/* Three lengths, one shown per breakpoint, so the address
                    fits its cell without measuring it in JS. Measured with
                    the two columns: the full address fits from md, 16
                    characters from sm, and below sm 16 leaves no margin at
                    375px, so phones get 8. */}
                <span className="min-w-0">
                    <Text
                        variant="small"
                        render={<span />}
                        className="whitespace-nowrap font-numeric-mono sm:hidden"
                        aria-hidden
                    >
                        {abbreviateAddress(authority.multisigAddress, 8, "..")}
                    </Text>
                    <Text
                        variant="small"
                        render={<span />}
                        className="hidden whitespace-nowrap font-numeric-mono sm:inline md:hidden"
                        aria-hidden
                    >
                        {abbreviateAddress(
                            authority.multisigAddress,
                            16,
                            "..."
                        )}
                    </Text>
                    <Text
                        variant="small"
                        render={<span />}
                        className="hidden whitespace-nowrap font-numeric-mono md:inline"
                        aria-hidden
                    >
                        {authority.multisigAddress}
                    </Text>
                    <span className="sr-only">{authority.multisigAddress}</span>
                </span>
                <ArrowShortUpRightIcon className="shrink-0" aria-hidden />
            </a>
        )
    }
];

/**
 * The Security tab: the Security Record.
 *
 * Five sections, in the order of the security copy doc: the audit dossier,
 * monitoring and response, the control examples, the authorities with the
 * program addresses, and the risks the controls do not cover.
 *
 * Control evidence comes from `securityEvidence.ts`, a dated snapshot.
 * The monitoring animation is illustrative, not live security telemetry.
 * A configured permission is a capability, not proof of governance.
 */
export function SecurityContent(): React.ReactElement {
    // Keep one audit selected while switching the outlined report card.
    const [activeAuditKey, setActiveAuditKey] = React.useState<string>(
        AUDITS[0].key
    );
    const activeAudit =
        AUDITS.find((audit) => audit.key === activeAuditKey) ?? AUDITS[0];
    return (
        <>
            {/* No rules between sections: the four after the audit dossier
                are opened by their own SectionHeading rule, and the dossier is
                set apart by whitespace (its mb-12 on top of the gap-16 step).
                The four themselves sit gap-24 apart.
                A stack rule above a heading would bracket it
                (layout/rule-edges). SectionHeading takes no id, so a wrapper
                div carries the id each section is labelled by. */}
            <SectionStack className="gap-16">
                <section
                    aria-labelledby="security-record-title"
                    className="mb-12 flex flex-col gap-24"
                >
                    <div className="flex flex-col gap-12 lg:flex-row">
                        <div className="min-w-0 lg:flex-2">
                            <div className="flex flex-col gap-4">
                                <Text
                                    variant="h1"
                                    render={<h2 id="security-record-title" />}
                                    className="text-balance"
                                >
                                    Evidence You Can Inspect
                                </Text>
                                <Text
                                    variant="small"
                                    className="text-foreground-muted"
                                >
                                    Three external security reviews were
                                    completed by two firms. The last two reviews
                                    reported zero critical and zero
                                    high-severity findings. The reports,
                                    remediation records, and acknowledged
                                    residual findings are public.
                                </Text>
                                <ToggleGroup
                                    aria-label="Audit Report"
                                    className="mt-12"
                                    spacing={2}
                                    requireSelection
                                    value={[activeAuditKey]}
                                    onValueChange={(values) => {
                                        const next = values[0];
                                        if (typeof next === "string")
                                            productEvent("stats:selected", {
                                                entry_point: "stats_page",
                                                section: "audits",
                                                target: next
                                            });
                                        if (typeof next === "string") {
                                            setActiveAuditKey(next);
                                        }
                                    }}
                                >
                                    {AUDITS.map((audit) => (
                                        <ToggleGroupItem
                                            key={audit.key}
                                            value={audit.key}
                                        >
                                            {audit.tabLabel ?? audit.auditor}
                                        </ToggleGroupItem>
                                    ))}
                                </ToggleGroup>
                            </div>
                        </div>

                        <div className="min-w-0 lg:flex-3">
                            <Card
                                key={activeAudit.key}
                                asElement="article"
                                surface="ground"
                                border="line"
                                topStripe={{ tone: "accent" }}
                                rounded={false}
                                aria-labelledby={`audit-${activeAudit.key}-title`}
                                className="flex min-w-0 flex-col lg:min-h-80"
                            >
                                <div className="relative h-16 shrink-0">
                                    {activeAudit.status ? (
                                        <Badge
                                            variant="fill"
                                            color="neutral"
                                            className="absolute left-6 top-6 sm:left-10 sm:top-10 lg:left-12"
                                        >
                                            <span className="sr-only">
                                                Status:{" "}
                                            </span>
                                            {activeAudit.status}
                                        </Badge>
                                    ) : null}
                                </div>

                                <div className="flex min-h-20 min-w-0 flex-1 items-center justify-center px-6 sm:px-10 lg:px-12">
                                    {activeAudit.logo ? (
                                        <p className="flex justify-center">
                                            <AuditLogo
                                                logo={activeAudit.logo}
                                                alt={activeAudit.auditor}
                                                className="h-20 w-auto max-w-full object-contain object-center"
                                            />
                                            <span className="sr-only">
                                                {" "}
                                                report {activeAudit.id}
                                            </span>
                                        </p>
                                    ) : (
                                        <Text
                                            variant="number-hero"
                                            render={<p />}
                                        >
                                            <span className="sr-only">
                                                {activeAudit.auditor}{" "}
                                                report{" "}
                                            </span>
                                            {activeAudit.id}
                                        </Text>
                                    )}
                                    <h3
                                        id={`audit-${activeAudit.key}-title`}
                                        className="sr-only"
                                    >
                                        {activeAudit.tabLabel ??
                                            activeAudit.auditor}{" "}
                                        Audit, {activeAudit.title}
                                    </h3>
                                </div>

                                <div className="flex h-22 w-full shrink-0 items-center justify-between gap-4 px-6 sm:px-10 lg:px-12">
                                    <Text
                                        variant="small"
                                        render={<span />}
                                        className="text-foreground-muted"
                                    >
                                        {activeAudit.date}
                                    </Text>
                                    {activeAudit.href ? (
                                        <Button
                                            variant="raised"
                                            nativeButton={false}
                                            trailing={
                                                <ArrowShortUpRightIcon className="size-5" />
                                            }
                                            render={
                                                <a
                                                    href={activeAudit.href}
                                                    onClick={() =>
                                                        productEvent(
                                                            "stats:link_clicked",
                                                            {
                                                                entry_point:
                                                                    "stats_page",
                                                                section:
                                                                    "security",
                                                                link_type:
                                                                    "audit_report",
                                                                target: activeAuditKey
                                                            }
                                                        )
                                                    }
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    aria-label={`Read Report, ${activeAudit.tabLabel ?? activeAudit.auditor} ${activeAudit.id}, opens in a new tab`}
                                                />
                                            }
                                        >
                                            Read Report
                                        </Button>
                                    ) : (
                                        <TooltipProvider delay={150}>
                                            <Tooltip>
                                                <TooltipTrigger
                                                    render={
                                                        <span
                                                            tabIndex={0}
                                                            className="inline-flex focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foreground-base"
                                                        />
                                                    }
                                                >
                                                    <Button
                                                        variant="raised"
                                                        trailing={
                                                            <ArrowShortUpRightIcon className="size-5" />
                                                        }
                                                        disabled
                                                    >
                                                        Read Report
                                                    </Button>
                                                </TooltipTrigger>
                                                <TooltipContent
                                                    variant="tint"
                                                    side="top"
                                                >
                                                    Not yet published
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    )}
                                </div>
                            </Card>
                        </div>
                    </div>
                </section>

                <SectionStack className="gap-24">
                    <section
                        aria-labelledby="monitoring-title"
                        className="flex flex-col gap-6"
                    >
                        <div id="monitoring-title">
                            <SectionHeading>
                                Monitoring and Response
                            </SectionHeading>
                        </div>
                        {/* Blockaid and STRIDE side by side from lg, each its
                        logo, then its strip scaled to the column, then its
                        description; stacked below lg. */}
                        <div className="mt-4 grid gap-16 lg:grid-cols-2 lg:gap-12">
                            <OnchainMonitoring />
                            <section
                                aria-labelledby="stride-title"
                                className="flex flex-col gap-2"
                            >
                                <h3
                                    id="stride-title"
                                    className="flex flex-col items-start gap-2"
                                >
                                    {/* The art reads "Asymmetric Research", not
                                    STRIDE, so the programme name rides along
                                    for screen readers. Height matches
                                    Blockaid's logo. */}
                                    <img
                                        src={asymmetricResearchLogo}
                                        alt="Asymmetric Research"
                                        className="h-5 w-auto md:h-7"
                                    />
                                    <span className="sr-only">STRIDE</span>
                                    <Text variant="small" render={<span />}>
                                        Operational Security Assessment
                                    </Text>
                                </h3>
                                <ControlInspection />
                                <Text
                                    variant="small"
                                    render={<p />}
                                    className="text-foreground-muted"
                                >
                                    Solomon is participating in STRIDE’s review
                                    of the controls surrounding the program:
                                    governance, signers, infrastructure,
                                    releases, monitoring, and incident response.
                                </Text>
                            </section>
                        </div>
                    </section>

                    <section
                        className="flex flex-col gap-8"
                        aria-labelledby="program-controls-title"
                    >
                        <header className="flex flex-col gap-6">
                            <div id="program-controls-title">
                                <SectionHeading>
                                    See What a Key Can Actually Do
                                </SectionHeading>
                            </div>
                            <Text
                                variant="small"
                                render={<p />}
                                className="text-foreground-muted"
                            >
                                Choose an action to trace who authorizes it,
                                what the USDv Controls Program checks, and when
                                the change can take effect. Restrictive actions
                                can be immediate. Changes that expand authority
                                are proposed with exact parameters and delayed.
                                A proposal cannot execute against a different
                                underlying state.
                            </Text>
                        </header>
                        <SecurityGovernanceWorkflows />
                        {/* The multisigs holding each authority close the
                        examples section; the program addresses get their own
                        section below, opened by its heading rule. */}
                        <section aria-label="Authorities" className="mt-4">
                            <TooltipProvider delay={150}>
                                <DataTable
                                    aria-label="Authority Multisigs"
                                    columns={AUTHORITY_COLUMNS}
                                    rows={AUTHORITY_ROWS}
                                    rowKey={(authority) => authority.id}
                                    density="regular"
                                />
                            </TooltipProvider>
                            {AUTHORITY_FOOTNOTE ? (
                                <Text
                                    id={AUTHORITY_FOOTNOTE_ID}
                                    variant="fine-print"
                                    render={<p />}
                                    className="mt-2 text-right text-foreground-muted"
                                >
                                    <sup>1</sup> {AUTHORITY_FOOTNOTE}
                                </Text>
                            ) : null}
                        </section>
                    </section>

                    <section
                        className="flex flex-col gap-8"
                        aria-labelledby="authorities-title"
                    >
                        <header className="flex flex-col gap-6">
                            <div id="authorities-title">
                                <SectionHeading>
                                    Who Controls the Controls
                                </SectionHeading>
                            </div>
                            <Text
                                variant="small"
                                render={<p />}
                                className="text-foreground-muted"
                            >
                                The addresses below are separate authorities
                                with different powers. An approval threshold
                                describes the signatures a multisig needs. the
                                USDv Controls Program imposes its own delays on
                                risk-increasing changes, regardless of how
                                quickly a multisig approves them.
                            </Text>
                        </header>
                        {/* The program and the token, framed with The Cut and
                        constrained to their content, under the section's
                        description. Below sm each label and its address
                        stack. */}
                        {/* One centered column sized to the callout. */}
                        <div className="mt-8 flex w-fit max-w-full min-w-0 flex-col gap-4 self-center">
                            <Callout className="flex min-w-0 flex-col gap-3 px-6 py-5">
                                {PROGRAM_ADDRESSES.map((entry) => (
                                    <div
                                        key={entry.address}
                                        className="flex flex-col gap-1 sm:flex-row sm:items-center"
                                    >
                                        <span className="flex shrink-0 items-center gap-2">
                                            {entry.icon ? (
                                                <img
                                                    src={entry.icon}
                                                    alt=""
                                                    // object-contain: the brandmark is
                                                    // wider than tall, and a shared
                                                    // square box keeps both labels
                                                    // starting at the same x.
                                                    className="size-6 object-contain"
                                                />
                                            ) : null}
                                            <Text
                                                variant="body"
                                                render={<span />}
                                            >
                                                {entry.name}:
                                            </Text>
                                        </span>
                                        <div className="min-w-0 sm:flex-1">
                                            <SecurityAddress
                                                address={entry.address}
                                                name={entry.name}
                                                explorerHref={
                                                    entry.explorerHref
                                                }
                                                explorerContext={entry.name}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </Callout>
                        </div>
                    </section>

                    <section
                        className="flex flex-col gap-6"
                        aria-labelledby="remaining-risks-title"
                    >
                        <div id="remaining-risks-title">
                            <SectionHeading>The Remaining Risks</SectionHeading>
                        </div>
                        <Text
                            variant="small"
                            render={<p />}
                            className="text-foreground-muted"
                        >
                            These controls constrain issuance and operator
                            actions. USDv also depends on reserve assets and
                            their issuers, Solana, market liquidity, and any
                            external application where USDv is used. Its
                            secondary-market price can move away from $1, and
                            direct redemption is subject to eligibility. Review
                            current reserves and the full risk disclosure
                            alongside this page.
                        </Text>
                    </section>
                </SectionStack>
            </SectionStack>
            <SecurityHelpBar />
        </>
    );
}
